/* src/components/settings/SettingsPage.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// --- Import Context hooks ---
// NOTE: Assuming getDefaultSettings is exported from SettingsContext
import { useSettings, getDefaultSettings } from '../../contexts/SettingsContext';
import { useProfile } from '../../contexts/ProfileContext';
// --- Import UI Components ---
import ConfirmationModal from '../ui/ConfirmationModal';
// --- MODIFICATION START: Remove UI component definitions, they are now in SettingSectionComponents.js ---
// LoadingSpinner, SectionCard, KeyBindingInput, ToggleSwitch, DropdownInput, SliderInput are removed
// --- MODIFICATION END ---
// --- MODIFICATION START: Import new child section components ---
import KeyBindingsSettings from './sections/KeyBindingsSettings';
import GameplayAudioSettings from './sections/GameplayAudioSettings';
import TimeZoneSettings from './sections/TimeZoneSettings';
import AdvancedSettingsSection from './sections/AdvancedSettingsSection';
import TrainingLevelSettings from './sections/TrainingLevelSettings';
// --- MODIFICATION END ---
// --- Import Routing & Icons ---
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    // faVolumeHigh, // No longer directly used, managed by child
    faSpinner, // Still used for Training Level save button
    // faKeyboard, // Moved to KeyBindingsSettings
    // faCog, // Moved to GameplayAudioSettings
    // faClock, // Moved to TimeZoneSettings
    // faBrain, // Moved to TrainingLevelSettings
    faSlidersH, // Used for page title
    // faFlask // Moved to AdvancedSettingsSection
} from '@fortawesome/free-solid-svg-icons';

// Import timezone options
import { TIMEZONE_OPTIONS } from '../../constants/timezoneData';

// --- MODIFICATION START: Import LANGUAGE_AUDIO_LETTERS for dynamic sample letters ---
import { LANGUAGE_AUDIO_LETTERS } from '../../constants/gameConstants';
const DEFAULT_ENGLISH_SAMPLE_LETTERS = ['B', 'D', 'K', 'P', 'T', 'X']; // Hardcoded fallback
// --- MODIFICATION END ---

// --- MODIFICATION START: Import audioManager ---
import audioManager from '../../utils/audioManager';
// --- MODIFICATION END ---

// --- MODIFICATION START: Reusable UI Components are removed from here ---
// LoadingSpinner, SectionCard, KeyBindingInput, ToggleSwitch, DropdownInput, SliderInput definitions removed
// --- MODIFICATION END ---

/**
 * SettingsPage Component
 * Allows users to configure application settings like key bindings, instant feedback,
 * audio voice selection, timezone, and manually adjust their N-Back training level.
 * It now orchestrates child components for each settings section.
 */
const SettingsPage = () => {
    // --- Contexts ---
    const { settings, updateSetting, isLoading: isLoadingAppSettings, error: appSettingsError, availableVoices } = useSettings();
    const { activeProfile, updateProfileProgress, isLoading: isLoadingProfile, error: profileError } = useProfile();
    const navigate = useNavigate();

    // --- Local State for UI ---
    const [settingKey, setSettingKey] = useState(null); // Tracks which key is currently being set
    const [localKeyBindings, setLocalKeyBindings] = useState({ visual: 'ArrowLeft', audio: 'ArrowRight' });
    const [localInstantFeedback, setLocalInstantFeedback] = useState(true);
    const [localSelectedVoiceId, setLocalSelectedVoiceId] = useState(''); // For audio voice dropdown
    const [localEnableExperimentalFeatures, setLocalEnableExperimentalFeatures] = useState(false); // New setting
    const [localTimezone, setLocalTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone); // Initialize with browser timezone
    const [isSavingAppSettings, setIsSavingAppSettings] = useState(false);
    const [appSaveStatus, setAppSaveStatus] = useState({ message: '', type: null, section: null }); // section identifies which card's status to show
    // --- MODIFICATION START: Remove modal state, add audio state ---
    // const [showAudioTest, setShowAudioTest] = useState(false); // Removed
    const [audioReady, setAudioReady] = useState(false);
    const [activeButton, setActiveButton] = useState(null); // For visual feedback on letter buttons
    // --- MODIFICATION END ---

    // Profile Level Settings
    const [localNBack, setLocalNBack] = useState('2'); // String value from N-Back dropdown
    const [localDifficultySliderValue, setLocalDifficultySliderValue] = useState(0); // Integer value 0-99 from slider
    const [isSavingLevel, setIsSavingLevel] = useState(false);
    const [levelSaveStatus, setLevelSaveStatus] = useState({ message: '', type: null });
    const [showLevelConfirmModal, setShowLevelConfirmModal] = useState(false);
    const [levelValidationError, setLevelValidationError] = useState('');

    // --- Effects ---

    // Initialize local App settings state from context or defaults
    useEffect(() => {
        const defaultSettings = getDefaultSettings();
        if (!isLoadingAppSettings && settings) {
            // Key Bindings
            const bindings = (settings.keyBindings && typeof settings.keyBindings.visual === 'string' && typeof settings.keyBindings.audio === 'string')
                             ? settings.keyBindings : defaultSettings.keyBindings;
            setLocalKeyBindings(bindings);

            // Instant Feedback
            const feedback = typeof settings.instantFeedback === 'boolean'
                             ? settings.instantFeedback : defaultSettings.instantFeedback;
            setLocalInstantFeedback(feedback);

            // Audio Voice Selection
            const currentVoice = settings.selectedVoiceId;
            const firstAvailableVoice = Object.keys(availableVoices || {})[0];
            const effectiveVoiceId = (currentVoice && availableVoices && availableVoices[currentVoice])
                                    ? currentVoice
                                    : (firstAvailableVoice || ''); // Fallback to first available or empty string
            setLocalSelectedVoiceId(effectiveVoiceId); // Use the determined ID

            // Timezone
            const timezone = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            setLocalTimezone(timezone);

            // Enable Experimental Features
            const experimental = typeof settings.enableExperimentalFeatures === 'boolean'
                                ? settings.enableExperimentalFeatures : defaultSettings.enableExperimentalFeatures;
            setLocalEnableExperimentalFeatures(experimental);

        } else if (!isLoadingAppSettings && !settings) {
             // Fallback to defaults if settings context is unavailable
             setLocalKeyBindings(defaultSettings.keyBindings);
             setLocalInstantFeedback(defaultSettings.instantFeedback);
             const firstAvailableVoice = Object.keys(availableVoices || {})[0]; // Need this here too
             setLocalSelectedVoiceId(firstAvailableVoice || ''); // Use the determined ID
             setLocalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
             setLocalEnableExperimentalFeatures(defaultSettings.enableExperimentalFeatures);
        }
    }, [isLoadingAppSettings, settings, availableVoices]); // Add availableVoices dependency

    // Initialize local Level settings state from active profile
    useEffect(() => {
        if (activeProfile) {
            // --- MODIFICATION START: Initialize level based on currentNBack, clamped to 2-20 ---
            const currentN = activeProfile.currentNBack ?? 2;
            const initialNBackClamped = Math.max(2, Math.min(20, currentN));
            setLocalNBack(String(initialNBackClamped));
            // --- MODIFICATION END ---
            const initialDifficulty = activeProfile.currentDifficulty ?? 0.0;
            // Ensure difficulty is within 0-0.99 range before scaling to slider value 0-99
            const clampedDifficulty = Math.max(0, Math.min(0.99, initialDifficulty));
            const sliderValue = Math.round(clampedDifficulty * 100);
            setLocalDifficultySliderValue(sliderValue);
            setLevelValidationError('');
        } else {
            // No active profile, default to 2
            setLocalNBack('2');
            setLocalDifficultySliderValue(0);
            setLevelValidationError('');
        }
    }, [activeProfile]);

    // Effect for capturing the key press
    useEffect(() => {
        if (!settingKey) return;
        const handleKeyDown = (e) => {
            e.preventDefault();
            const newKey = e.key;
             if (newKey === 'Escape' || newKey === 'Tab') {
                 setSettingKey(null);
                 return;
             }
            setLocalKeyBindings(prev => ({ ...prev, [settingKey]: newKey }));
            setSettingKey(null);
            // Do not auto-save here, user must click the save button for keybindings
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [settingKey]);

    // --- MODIFICATION START: Effect to initialize AudioManager ---
    useEffect(() => {
        const ready = audioManager.init();
        setAudioReady(ready);
        if (!ready) {
            console.error("SettingsPage: AudioManager initialization failed.");
        }
    }, []); // Run only on mount
    // --- MODIFICATION END ---

    // --- Handlers ---

    // Activate key setting mode
    const handleStartSettingKey = useCallback((keyType) => {
        if (isSavingAppSettings || isSavingLevel) return; // Don't allow changing while saving
        if (settingKey === keyType) {
            setSettingKey(null); // Toggle off if clicking the same button
            return;
        }
        setSettingKey(keyType);
        setAppSaveStatus({ message: '', type: null }); // Clear previous status when starting to set
    }, [settingKey, isSavingAppSettings, isSavingLevel]);

    // --- MODIFICATION START: Remove toggleAudioTest, add audio handlers ---
    // Toggle the audio test modal visibility
    // const toggleAudioTest = useCallback(() => { // Removed
    //     setShowAudioTest(prev => !prev);
    // }, []);

    const handleResumeAudio = useCallback(async () => {
        console.log("SettingsPage: Attempting to resume audio context...");
        const resumed = await audioManager.resume();
        if (resumed) {
            console.log("SettingsPage: Audio context resumed successfully.");
            setAudioReady(true);
            // Optionally play a confirmation sound if desired, but not requested
            // audioManager.playFeedbackSound('correct');
        } else {
            console.error("SettingsPage: Failed to resume audio context.");
        }
    }, []); // No dependencies needed

    // Determine if UI should be disabled generally (e.g., saving, setting key)
    const isAnySaving = isSavingAppSettings || isSavingLevel;
    const isKeySettingInProgress = !!settingKey;
    const isGeneralSectionDisabled = isAnySaving || isKeySettingInProgress;

    const handleTestLetter = useCallback(async (letter) => {
        // Prevent testing if general section is disabled
        if (isGeneralSectionDisabled) return;

        setActiveButton(`letter-${letter}`);
        const resumed = await audioManager.resume(); // Attempt resume on interaction
        setAudioReady(audioManager.getAudioContext()?.state === 'running'); // Update ready state after attempt

        if (resumed) {
            console.log(`SettingsPage: Playing letter ${letter} using voice: ${localSelectedVoiceId || 'default'}...`);
            audioManager.playItem(letter, 0.5, { voiceId: localSelectedVoiceId }); // Use local state for voice ID
        } else {
            console.warn("SettingsPage: Cannot play letter, audio context potentially not running.");
            // Optionally show a message to click "Activate Audio"
        }
        // Use a timer to clear the active state for visual feedback
        const timerId = setTimeout(() => setActiveButton(null), 300);
        // Note: No cleanup for this short timer needed in this context
    }, [localSelectedVoiceId, isGeneralSectionDisabled]); // Depend on selected voice and disabled state
    // --- MODIFICATION END ---


    // --- REFACTORED: Handle saving specific App Settings ---
    // Accepts an object of settings to save and a section identifier for status messages
    const handleSaveAppSettings = useCallback(async (settingsToSave, sectionIdentifier) => {
        if (settingKey) {
            setAppSaveStatus({ message: 'Finish setting key before saving.', type: 'error', section: sectionIdentifier });
            return;
        }

        // --- Validation specific to the settings being saved ---
        let validationError = null;
        if (settingsToSave.hasOwnProperty('keyBindings')) {
            const { visual, audio } = settingsToSave.keyBindings;
            if (!visual || !audio) validationError = 'Both Visual and Audio keys must be set.';
            else if (visual === audio) validationError = 'Visual and Audio keys cannot be the same.';
        }
        if (settingsToSave.hasOwnProperty('selectedVoiceId') && !settingsToSave.selectedVoiceId) {
             // Allow null/empty if no voices are available
             if (availableVoices && Object.keys(availableVoices).length > 0) {
                 validationError = 'Please select an audio voice.';
             }
        }
        if (settingsToSave.hasOwnProperty('timezone') && !settingsToSave.timezone) {
            validationError = 'Please select a time zone.';
        }
        if (settingsToSave.hasOwnProperty('enableExperimentalFeatures') && typeof settingsToSave.enableExperimentalFeatures !== 'boolean') {
            validationError = 'Invalid value for experimental features setting.';
        }

        if (validationError) {
            setAppSaveStatus({ message: validationError, type: 'error', section: sectionIdentifier });
            return;
        }

        // --- Proceed with saving ---
        setIsSavingAppSettings(true);
        setAppSaveStatus({ message: '', type: null, section: null }); // Clear status before saving

        try {
            // Create an array of promises for each setting update
            const updatePromises = Object.entries(settingsToSave).map(([key, value]) =>
                updateSetting(key, value) // Assumes updateSetting handles its own logic and returns a promise/is async
            );

            // Wait for all updates to complete
            await Promise.all(updatePromises);

            // Determine success message based on section
            let successMessage = 'Settings saved!';
            if (sectionIdentifier === 'keyBindings') successMessage = 'Key bindings saved!';
            else if (sectionIdentifier === 'gameplay') successMessage = 'Gameplay & audio settings saved!';
            else if (sectionIdentifier === 'timezone') successMessage = 'Time zone saved!';
            else if (sectionIdentifier === 'advanced') successMessage = 'Advanced settings saved!';

            setAppSaveStatus({ message: successMessage, type: 'success', section: sectionIdentifier });

            // Clear success message after delay
            setTimeout(() => setAppSaveStatus(prev =>
                prev.type === 'success' && prev.section === sectionIdentifier
                ? { message: '', type: null, section: null }
                : prev
            ), 3000);

        } catch (err) {
            console.error(`Error saving app settings for section ${sectionIdentifier}:`, err);
            setAppSaveStatus({
                message: appSettingsError || `Failed to save ${sectionIdentifier} settings. Error: ${err.message}`,
                type: 'error',
                section: sectionIdentifier
            });
        } finally {
            setIsSavingAppSettings(false);
        }
    }, [settingKey, updateSetting, appSettingsError, availableVoices]); // Dependencies: settingKey, updateSetting function, context error, availableVoices

    // --- MODIFICATION START: Wrapper save handlers for child components ---
    const handleSaveKeyBindings = useCallback(() => {
        handleSaveAppSettings({ keyBindings: localKeyBindings }, 'keyBindings');
    }, [handleSaveAppSettings, localKeyBindings]);

    const handleSaveGameplayAudio = useCallback(() => {
        handleSaveAppSettings({
            instantFeedback: localInstantFeedback,
            selectedVoiceId: localSelectedVoiceId
        }, 'gameplay');
    }, [handleSaveAppSettings, localInstantFeedback, localSelectedVoiceId]);

    const handleSaveTimeZone = useCallback(() => {
        handleSaveAppSettings({ timezone: localTimezone }, 'timezone');
    }, [handleSaveAppSettings, localTimezone]);

    const handleSaveAdvancedSettings = useCallback(() => {
        handleSaveAppSettings({ enableExperimentalFeatures: localEnableExperimentalFeatures }, 'advanced');
    }, [handleSaveAppSettings, localEnableExperimentalFeatures]);
    // --- MODIFICATION END ---

    // --- Handlers for Manual Level Change ---

    const validateLevelInputs = useCallback(() => {
        if (!activeProfile) {
            setLevelValidationError('No active profile selected.');
            return false;
        }
        // Add validation if needed (e.g., ensure NBack is a number)
        setLevelValidationError('');
        return true;
    }, [activeProfile]);

    const handleRequestLevelChange = useCallback(() => {
        setLevelSaveStatus({ message: '', type: null });
        if (validateLevelInputs()) {
            setShowLevelConfirmModal(true);
        }
    }, [validateLevelInputs]);

    const handleConfirmLevelChange = useCallback(async () => {
        if (!validateLevelInputs()) {
            setShowLevelConfirmModal(false);
            return;
        }
        setIsSavingLevel(true);
        setLevelSaveStatus({ message: '', type: null });
        setShowLevelConfirmModal(false);
        const nBackToSave = parseInt(localNBack, 10);
        const difficultyToSave = localDifficultySliderValue / 100.0;

        try {
            console.log(`SettingsPage: Calling updateProfileProgress for ${activeProfile?.id} with N=${nBackToSave}, Diff=${difficultyToSave.toFixed(2)}`);
            // Ensure activeProfile.id is valid before calling
            if (!activeProfile?.id) {
                throw new Error("Active profile ID is missing.");
            }
            const success = await updateProfileProgress(activeProfile.id, {
                currentNBack: nBackToSave,
                currentDifficulty: difficultyToSave
            });

            if (success) {
                setLevelSaveStatus({ message: 'Training level updated!', type: 'success' });
                setTimeout(() => setLevelSaveStatus(prev => prev.type === 'success' ? { message: '', type: null } : prev), 3500);
            } else {
                // Use specific error from context if available, otherwise generic message
                setLevelSaveStatus({ message: profileError || 'Failed to save level. Please try again.', type: 'error' });
            }
        } catch (err) {
            console.error("Error directly calling updateProfileProgress:", err);
            setLevelSaveStatus({ message: `Error updating level: ${err.message}`, type: 'error' });
        } finally {
            setIsSavingLevel(false);
        }
    }, [activeProfile, localNBack, localDifficultySliderValue, updateProfileProgress, validateLevelInputs, profileError]);

    const handleCancelLevelChange = useCallback(() => {
        setShowLevelConfirmModal(false);
        setLevelSaveStatus({ message: '', type: null }); // Clear status on cancel
    }, []);

    // --- Derived Values ---

    // N-Back options
    const nBackOptions = useMemo(() => {
        const options = [];
        // --- MODIFICATION START: Generate options from 2 to 20 ---
        const MIN_LEVEL = 2;
        const MAX_LEVEL = 20;
        for (let i = MIN_LEVEL; i <= MAX_LEVEL; i++) {
            options.push({ value: String(i), label: String(i) });
        }
        // --- MODIFICATION END ---
        return options;
    }, []); // No dependency on activeProfile needed for a fixed range

    // Displayed combined level
    const displayedCombinedLevel = useMemo(() => {
        const nBackNum = parseInt(localNBack, 10) || 0;
        // Ensure slider value is treated as a number between 0 and 99
        const difficulty = Math.max(0, Math.min(99, localDifficultySliderValue)) / 100.0;
        return (nBackNum + difficulty).toFixed(2);
    }, [localNBack, localDifficultySliderValue]);

     // Format available voices for the DropdownInput component
     const voiceOptions = useMemo(() => {
        if (!availableVoices || Object.keys(availableVoices).length === 0) return [];
        return Object.entries(availableVoices).map(([id, voice]) => ({
            value: id, // e.g., "google-chirp3-hd-en-us-charon"
            label: voice.name || id // Use name if available, otherwise fallback to ID
        }));
    }, [availableVoices]);

    // Format current local time in selected timezone
    const currentLocalTime = useMemo(() => {
        try {
            return new Date().toLocaleTimeString([], {
                timeZone: localTimezone,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            console.error("Error formatting time for timezone:", localTimezone, e);
            // Fallback to browser default if selected timezone is invalid
            try {
                 return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
            } catch { return "Invalid Time"; }
        }
    }, [localTimezone]);

    // Format current local date in selected timezone
    const currentLocalDate = useMemo(() => {
        try {
            return new Date().toLocaleDateString([], {
                timeZone: localTimezone,
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
            });
        } catch (e) {
            console.error("Error formatting date for timezone:", localTimezone, e);
             // Fallback to browser default if selected timezone is invalid
            try {
                return new Date().toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
            } catch { return "Invalid Date"; }
        }
    }, [localTimezone]);

    // --- Render Logic ---

    const isLoading = isLoadingAppSettings || isLoadingProfile;

    if (isLoading) {
        return <div className="p-6 text-center text-gray-500">Loading settings and profile...</div>;
    }

    // Use the already defined isGeneralSectionDisabled for disabling audio test buttons too
    // const isAnySaving = isSavingAppSettings || isSavingLevel; // Already defined
    // const isKeySettingInProgress = !!settingKey; // Already defined
    // const isGeneralSectionDisabled = isAnySaving || isKeySettingInProgress; // Already defined
    const isLevelSectionDisabled = isAnySaving || isKeySettingInProgress || !activeProfile; // Level section also disabled if no profile

    // --- MODIFICATION START: Dynamically determine sample letters based on selected voice/language ---
    const dynamicSampleLetters = useMemo(() => {
        const availableLangKeys = Object.keys(LANGUAGE_AUDIO_LETTERS);
        let langCode = 'en'; // Default to 'en' for letter selection

        if (localSelectedVoiceId) {
            const parts = localSelectedVoiceId.split('-');
            let foundCode = null;

            // Attempt to match common voice ID patterns to language keys in LANGUAGE_AUDIO_LETTERS
            // 1. "lang-REGION" style (e.g., "cmn-TW", "es-ES")
            if (parts.length >= 2) {
                const testCode = `${parts[0]}-${parts[1]}`;
                if (availableLangKeys.includes(testCode)) foundCode = testCode;
            }
            // 2. "lang" style (e.g., "en", "de") - if not already found by "lang-REGION"
            if (!foundCode && parts.length >= 1) {
                const testCode = parts[0];
                if (availableLangKeys.includes(testCode)) foundCode = testCode;
            }
            // 3. Google-prefixed voices: "google-lang-REGION" or "google-lang"
            if (!foundCode && parts[0] === 'google') {
                if (parts.length >= 3) { // Try "google-lang-REGION" -> "lang-REGION"
                    const testCode = `${parts[1]}-${parts[2]}`;
                    if (availableLangKeys.includes(testCode)) foundCode = testCode;
                }
                if (!foundCode && parts.length >= 2) { // Try "google-lang" -> "lang"
                    const testCode = parts[1];
                    if (availableLangKeys.includes(testCode)) foundCode = testCode;
                }
            }
            if (foundCode) langCode = foundCode;
        }

        // Ensure langCode is valid and present in LANGUAGE_AUDIO_LETTERS.
        // If determined langCode is not found, fallback to 'en' if 'en' letters are available.
        if (!LANGUAGE_AUDIO_LETTERS[langCode] && availableLangKeys.includes('en')) {
            langCode = 'en';
        } else if (!LANGUAGE_AUDIO_LETTERS[langCode]) {
            // If neither the derived langCode nor 'en' is valid for letters, use hardcoded default.
            return DEFAULT_ENGLISH_SAMPLE_LETTERS;
        }

        const letterData = LANGUAGE_AUDIO_LETTERS[langCode];
        if (!letterData || !letterData.letters || !Array.isArray(letterData.letters) || letterData.letters.length === 0) {
            return DEFAULT_ENGLISH_SAMPLE_LETTERS; // Malformed or empty letter data for the language
        }

        // A simple strategy: take the first `maxSamples` unique letters for the language.
        const allUniqueLettersForLang = [...new Set(letterData.letters.flat())];
        const maxSamples = 6;
        const samples = allUniqueLettersForLang.slice(0, maxSamples);
        
        return samples.length > 0 ? samples : DEFAULT_ENGLISH_SAMPLE_LETTERS;

    }, [localSelectedVoiceId]); // Recalculate when the selected voice ID changes
    // --- MODIFICATION END ---


    return (
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            <div className="max-w-2xl mx-auto relative">
                {/* Page Header */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faSlidersH} className="mr-3 text-indigo-600 h-6 w-6" /> {/* Added icon */}
                        Profile Settings
                    </h1>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1"
                    >
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Display Global Context Errors */}
                {appSettingsError && <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">{appSettingsError}</p>}
                {profileError && <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">{profileError}</p>}

                {/* --- MODIFICATION START: Use child components for sections --- */}
                <KeyBindingsSettings
                    localKeyBindings={localKeyBindings}
                    settingKey={settingKey}
                    onStartSettingKey={handleStartSettingKey}
                    isGeneralSectionDisabled={isGeneralSectionDisabled}
                    isSavingAppSettings={isSavingAppSettings}
                    appSaveStatus={appSaveStatus}
                    onSaveKeyBindings={handleSaveKeyBindings}
                />

                <GameplayAudioSettings
                    localInstantFeedback={localInstantFeedback}
                    onInstantFeedbackChange={setLocalInstantFeedback}
                    localSelectedVoiceId={localSelectedVoiceId}
                    onSelectedVoiceIdChange={(e) => setLocalSelectedVoiceId(e.target.value)}
                    voiceOptions={voiceOptions}
                    isGeneralSectionDisabled={isGeneralSectionDisabled}
                    audioReady={audioReady}
                    onResumeAudio={handleResumeAudio}
                    sampleLetters={dynamicSampleLetters} // Use dynamic letters
                    onTestLetter={handleTestLetter}
                    activeButton={activeButton}
                    isSavingAppSettings={isSavingAppSettings}
                    appSaveStatus={appSaveStatus}
                    onSaveGameplayAudio={handleSaveGameplayAudio}
                />

                <TimeZoneSettings
                    localTimezone={localTimezone}
                    onTimezoneChange={(e) => setLocalTimezone(e.target.value)}
                    timezoneOptions={TIMEZONE_OPTIONS}
                    currentLocalTime={currentLocalTime}
                    currentLocalDate={currentLocalDate}
                    isGeneralSectionDisabled={isGeneralSectionDisabled}
                    isSavingAppSettings={isSavingAppSettings}
                    appSaveStatus={appSaveStatus}
                    onSaveTimeZone={handleSaveTimeZone}
                />

                <AdvancedSettingsSection
                    localEnableExperimentalFeatures={localEnableExperimentalFeatures}
                    onEnableExperimentalFeaturesChange={setLocalEnableExperimentalFeatures}
                    isGeneralSectionDisabled={isGeneralSectionDisabled}
                    isSavingAppSettings={isSavingAppSettings}
                    appSaveStatus={appSaveStatus}
                    onSaveAdvancedSettings={handleSaveAdvancedSettings}
                />

                <TrainingLevelSettings
                    activeProfile={activeProfile}
                    localNBack={localNBack}
                    onNBackChange={(e) => setLocalNBack(e.target.value)}
                    nBackOptions={nBackOptions}
                    localDifficultySliderValue={localDifficultySliderValue}
                    onDifficultySliderChange={(e) => setLocalDifficultySliderValue(parseInt(e.target.value, 10))}
                    displayedCombinedLevel={displayedCombinedLevel}
                    isLevelSectionDisabled={isLevelSectionDisabled}
                    levelValidationError={levelValidationError}
                    isSavingLevel={isSavingLevel}
                    levelSaveStatus={levelSaveStatus}
                    onRequestLevelChange={handleRequestLevelChange}
                    // faSpinner is directly used in TrainingLevelSettings, so no prop needed here
                />
                {/* --- MODIFICATION END --- */}


                {/* --- MODIFICATION START: Remove Audio Test Modal Rendering --- */}
                {/* {showAudioTest && ( ... )} */}
                {/* --- MODIFICATION END --- */}


                {/* Level Change Confirmation Modal Rendering */}
                <ConfirmationModal
                    isOpen={showLevelConfirmModal}
                    title="Confirm Manual Level Change"
                    // Pass message content as children
                    onConfirm={handleConfirmLevelChange}
                    onClose={handleCancelLevelChange}
                    confirmText="Change Level"
                    cancelText="Cancel"
                    isDestructive={false} // Not a destructive action
                 >
                    <p className="text-sm text-gray-600">Recommendation: For optimal results and accurate progress tracking, allow the system to automatically adjust your N-Back level based on performance.</p>
                    <p className="text-sm text-gray-600 mt-2">Manually setting the level can be useful for experimentation or specific goals, but may disrupt the intended adaptive training flow.</p>
                    <p className="text-sm text-gray-800 font-medium mt-3">Continue with manual change?</p>
                 </ConfirmationModal>

            </div> {/* End max-w-2xl container */}
        </div> // End page wrapper
    );
};

export default SettingsPage;